/*!
 * Bind custom web components e.g. <custom-binding> 🥊  tko.provider.component@4.0.0-alpha5c
 * (c) The Knockout.js Team - https://tko.io/
 * License: MIT (http://www.opensource.org/licenses/mit-license.php)
 */

import { tagNameLower, objectMap } from 'tko.utils';
import registry from 'tko.utils.component';
import { unwrap, isWriteableObservable } from 'tko.observable';
import { computed } from 'tko.computed';
import { Provider } from 'tko.provider';
import { Parser } from 'tko.utils.parser';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = Object.setPrototypeOf ||
    ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
    function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var ComponentProvider = /** @class */ (function (_super) {
    __extends(ComponentProvider, _super);
    function ComponentProvider() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    Object.defineProperty(ComponentProvider.prototype, "FOR_NODE_TYPES", {
        get: function () { return [1]; } // document.ELEMENT_NODE
        ,
        enumerable: true,
        configurable: true
    });
    /**
     * Convert <slot name='X'> to <!-- ko slot: 'X' --><!-- /ko -->
     * @param {HTMLElement} node
     */
    ComponentProvider.prototype.preprocessNode = function (node) {
        if (node.tagName === 'SLOT') {
            var parent_1 = node.parentNode;
            var slotName = node.getAttribute('name') || '';
            var openNode = document.createComment("ko slot: \"" + slotName + "\"");
            var closeNode = document.createComment('/ko');
            parent_1.insertBefore(openNode, node);
            parent_1.insertBefore(closeNode, node);
            parent_1.removeChild(node);
            return [openNode, closeNode];
        }
    };
    ComponentProvider.prototype.nodeHasBindings = function (node) {
        return Boolean(this.getComponentNameForNode(node));
    };
    ComponentProvider.prototype.getBindingAccessors = function (node, context) {
        var _this = this;
        var componentName = this.getComponentNameForNode(node);
        if (!componentName) {
            return;
        }
        var component = function () { return ({
            name: componentName,
            params: _this.getComponentParams(node, context)
        }); };
        return { component: component };
    };
    ComponentProvider.prototype.getComponentNameForNode = function (node) {
        if (node.nodeType !== node.ELEMENT_NODE) {
            return;
        }
        var tagName = tagNameLower(node);
        if (registry.isRegistered(tagName)) {
            var hasDash = tagName.includes('-');
            var isUnknownEntity = ('' + node) === '[object HTMLUnknownElement]';
            if (hasDash || isUnknownEntity) {
                return tagName;
            }
        }
    };
    ComponentProvider.prototype.getComponentParams = function (node, context) {
        var _this = this;
        var parser = new Parser(node, context, this.globals);
        var paramsString = (node.getAttribute('params') || '').trim();
        var accessors = parser.parse(paramsString, context, node);
        if (!accessors || Object.keys(accessors).length === 0) {
            return { $raw: {} };
        }
        var $raw = objectMap(accessors, function (value) { return computed(value, null, { disposeWhenNodeIsRemoved: node }); });
        var params = objectMap($raw, function (v) { return _this.makeParamValue(node, v); });
        return Object.assign({ $raw: $raw }, params);
    };
    ComponentProvider.prototype.makeParamValue = function (node, paramValueComputed) {
        var paramValue = paramValueComputed.peek();
        // Does the evaluation of the parameter value unwrap any observables?
        if (!paramValueComputed.isActive()) {
            // No it doesn't, so there's no need for any computed wrapper. Just pass through the supplied value directly.
            // Example: "someVal: firstName, age: 123" (whether or not firstName is an observable/computed)
            return paramValue;
        }
        // Yes it does. Supply a computed property that unwraps both the outer (binding expression)
        // level of observability, and any inner (resulting model value) level of observability.
        // This means the component doesn't have to worry about multiple unwrapping. If the value is a
        // writable observable, the computed will also be writable and pass the value on to the observable.
        var isWriteable = isWriteableObservable(paramValue);
        return computed({
            read: function () { return unwrap(paramValueComputed()); },
            write: isWriteable ? function (v) { return paramValueComputed()(v); } : null,
            disposeWhenNodeIsRemoved: node
        });
    };
    return ComponentProvider;
}(Provider));

export { ComponentProvider };
//# sourceMappingURL=tko.provider.component.js.map
